import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  FaDownload,
  FaEdit,
  FaEye,
  FaMailBulk,
  FaPlus,
  FaPrint,
  FaShareAlt,
  FaTrashAlt,
} from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import BasicModal from "../../UI/BasicModal";
import ListPagination from "../../UI/ListPagination";
import ConfirmDialog from "../../UI/ConfirmDialog";
import PageHeader from "../../UI/PageHeader";
import SelectField from "../../UI/SelectField";
import { COPY_TYPE, itemsPerPage } from "../../constant";
import {
  getErrorMessage,
  getSuccessMessage,
  getTemplateFunction
} from "../../functions";
import {
  deleteInvoice,
  downloadPdf,
  getAllInvoice,
  sendToMail,
} from "../../services/invoiceApi";
import CreateInvoice from "./components/CreateInvoice";
import EditInvoice from "./components/EditInvoice";
import LivePreview from "./components/LivePreview";

const Invoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState({});
  const [copyType, setCopyType] = useState(COPY_TYPE[1]);
  const [showPreview, setShowPreview] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const { auth } = useSelector((state) => state);
  const companyDetails = useSelector(
    (state) => state.companyDetails.companyDetails
  );
  const isMobileView = useSelector((state) => state.mobileView.isMobileView);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const filteredProducts = invoices.filter(
    (product) =>
      (product.invoiceNumber &&
        product.invoiceNumber
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product.customerDetails.name &&
        product.customerDetails.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product.customerDetails.email &&
        product.customerDetails.email
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product?.customerDetails?.gst &&
        product.customerDetails.gst
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product?.customerDetails?.address &&
        product.customerDetails.address
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product?.customerDetails?.phoneNumber &&
        product.customerDetails.phoneNumber
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product?.customerDetails?.whatsappNumber &&
        product.customerDetails.whatsappNumber
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product.billTo &&
        product.billTo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.eWayBillNo &&
        product.eWayBillNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.stateOfSupply &&
        product.stateOfSupply
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (product.billType &&
        product.billType.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const pageCount = Math.ceil(filteredProducts.length / itemsPerPage);

  const toggleRefresh = async () => {
    await fetchAllInvoices();
  };
  const fetchAllInvoices = async () => {
    try {
      setLoading(true);
      const res = await getAllInvoice();
      setInvoices(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllInvoices();
  }, []);

  const printPDF = (invoice) => {
    if (!copyType) {
      return toast.error("Please select a copy type");
    }

    const html = getTemplateFunction(companyDetails.defaultTemplate)(
      invoice,
      copyType.name
    );

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const iframeDocument = iframe.contentDocument;
    iframeDocument.open();
    iframeDocument.write(html);
    iframeDocument.close();

    setTimeout(() => {
      iframe.contentWindow.print();
      document.body.removeChild(iframe);
    }, 1000);
  };

  const handleEditNavigate = (invoice) => {
    setInvoiceToEdit(invoice);
    setIsEdit(true);
  };
  const handleSelectedInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPreview(true);
  };

  const handleCreateNew = () => {
    setIsCreate(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
      setDeletingInvoice(true);
      await deleteInvoice(invoiceToDelete._id);
      setInvoices((prev) => prev.filter((inv) => inv._id !== invoiceToDelete._id));
      toast.success("Invoice deleted");
      setInvoiceToDelete(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeletingInvoice(false);
    }
  };

  const handleSentToMail = async (invoice) => {
    try {
      if (!copyType) {
        return toast.error("Please select a copy type");
      }
      setLoading(true);
      const res = await sendToMail({
        html: getTemplateFunction(companyDetails.defaultTemplate)(
          invoice,
          copyType.name
        ),
        from: auth?.user?.email,
        to: invoice?.customerDetails?.email,
        text: `Contact Information \nName:${auth.user?.firstName} ${auth.user?.lastName}\nEmail : ${auth.user?.email} \nPhone Number: ${auth.user?.phoneNumber}`,
        replyTo: auth?.user?.email,
        subject: `Invoice from ${companyDetails.businessName}`,
      });
      toast.success(getSuccessMessage(res));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      if (!copyType) {
        return toast.error("Please select a copy type");
      }
      setLoading(true);

      const response = await downloadPdf({
        html: getTemplateFunction(companyDetails.defaultTemplate)(
          invoice,
          copyType.name
        ),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Create a blob from the response
      const blob = await response.blob();

      // Create a temporary anchor element to trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "invoice.pdf"); // or any other filename you prefer
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    setCurrentPage(selectedPage);
  };
  const offset = currentPage * itemsPerPage;
  const endOffset = offset + itemsPerPage;

  const currentProducts = filteredProducts.slice(
    offset,
    Math.min(endOffset, filteredProducts.length)
  );

  const handleShare = async (invoiceId) => {
    if (!copyType) {
      return toast.error("Please select a copy type");
    }
    const textToShare =
      `${process.env.REACT_APP_FRONTEND_URL}/invoice/?invoiceId=${invoiceId}&copyType=${copyType.name}`.replace(
        " ",
        "%20"
      );
    try {
      // Check if the browser supports navigator.share
      if (navigator.share) {
        await navigator.share({
          title: "Share Invoice Link",
          text: textToShare,
        });
      } else {
        // Fallback for browsers that do not support navigator.share
        const tempInput = document.createElement("input");
        tempInput.value = textToShare;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
        alert("Text copied to clipboard");
      }
    } catch (error) {
      toast.error("Error sharing. Please try again.");
    }
  };

  const isEmptyInvoices = invoices.length === 0;
  const noSearchResults =
    !isEmptyInvoices && filteredProducts.length === 0;

  if (loading) {
    return (
      <Box>
        <Skeleton height="36px" width={{ base: "70%", md: "40%" }} mb={3} />
        <Skeleton height="20px" width={{ base: "90%", md: "55%" }} mb={8} />
        <Skeleton height="44px" width="100%" maxW="md" mb={6} />
        <Skeleton height="240px" borderRadius="lg" />
      </Box>
    );
  }

  return (
    <div>
      <BasicModal
        isOpen={isCreate}
        onClose={() => setIsCreate(false)}
        title="Create invoice"
      >
        <CreateInvoice
          setIsCreate={setIsCreate}
          invoices={invoices}
          setInvoices={setInvoices}
        />
      </BasicModal>
      <BasicModal
        isOpen={isEdit}
        onClose={() => setIsEdit(false)}
        title="Edit invoice"
      >
        <EditInvoice
          invoice={invoiceToEdit}
          toggleRefresh={toggleRefresh}
          setIsEdit={setIsEdit}
        />
      </BasicModal>
      <BasicModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Invoice preview"
      >
        <LivePreview invoice={selectedInvoice} />
      </BasicModal>
      <PageHeader
        title="Invoices"
        description="Create, print, and send GST invoices from one place."
        actions={
          <Button
            colorScheme="brand"
            onClick={handleCreateNew}
            leftIcon={<FaPlus size={14} />}
          >
            New invoice
          </Button>
        }
      />
      <div className="mb-6 max-w-md">
        <label
          htmlFor="invoice-search"
          className="mb-1.5 block text-sm font-medium text-secondary"
        >
          Search
        </label>
        <input
          id="invoice-search"
          type="search"
          placeholder="Invoice number, customer, GST…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="min-h-10 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-secondary shadow-sm transition-colors duration-200 placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {isEmptyInvoices ? (
        <Box
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="gray.300"
          borderRadius="xl"
          py={16}
          px={6}
          textAlign="center"
          bg="white"
        >
          <p className="text-base font-medium text-gray-800">
            No invoices yet
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Create your first invoice to download PDFs, send by email, and
            share a payment link.
          </p>
          <Button colorScheme="brand" mt={6} onClick={handleCreateNew}>
            Create invoice
          </Button>
        </Box>
      ) : noSearchResults ? (
        <Box py={12} textAlign="center" fontSize="sm" color="gray.600">
          No invoices match your search. Try another invoice number or
          customer name.
        </Box>
      ) : (
      <div className="overflow-y-auto">
        {isMobileView ? (
          <div className="flex flex-col gap-4 p-4">
            {currentProducts.map((item) => (
              <div key={item._id} className="bg-gray-100 p-2  shadow-md">
                <div className="font-bold">
                  Invoice Number: {item.invoiceNumber}
                </div>
                <div className="flex flex-col gap-1">
                  <div>
                    <span className="font-semibold">Customer:</span>{" "}
                    {item.customerDetails.name}
                  </div>
                  <div>
                    <span className="font-semibold">Date of Invoice:</span>{" "}
                    {new Date(item.invoiceDate).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2 items-center">
                    <SelectField
                      inputClass={"w-full"}
                      value={{
                        label: copyType.name,
                        value: copyType,
                      }}
                      options={COPY_TYPE}
                      onChange={({ value }) => {
                        setCopyType(value);
                      }}
                      selectStyles={{
                        control: (provided) => ({
                          ...provided,
                          width: "150px",
                        }),
                      }}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {isEdit ? (
                      <IconButton
                        aria-label="Cancel"
                        variant="ghost"
                        icon={<MdCancel size={18} />}
                        onClick={() => setIsEdit(false)}
                      />
                    ) : (
                      <>
                        {!isMobileView ? (
                          <IconButton
                            aria-label="Preview invoice"
                            variant="ghost"
                            colorScheme="brand"
                            icon={<FaEye size={18} />}
                            onClick={() => handleSelectedInvoice(item)}
                          />
                        ) : null}
                        <IconButton
                          aria-label="Share link"
                          variant="ghost"
                          colorScheme="brand"
                          icon={<FaShareAlt size={18} />}
                          onClick={() => handleShare(item._id)}
                        />
                        <IconButton
                          aria-label="Print"
                          variant="ghost"
                          colorScheme="gray"
                          icon={<FaPrint size={18} />}
                          onClick={() => printPDF(item)}
                        />
                        <IconButton
                          aria-label="Email invoice"
                          variant="ghost"
                          colorScheme="gray"
                          icon={<FaMailBulk size={18} />}
                          onClick={() => handleSentToMail(item)}
                        />
                        <IconButton
                          aria-label="Download PDF"
                          variant="ghost"
                          colorScheme="gray"
                          icon={<FaDownload size={18} />}
                          onClick={() => handleDownloadPdf(item)}
                        />
                        <IconButton
                          aria-label="Edit invoice"
                          variant="ghost"
                          colorScheme="brand"
                          icon={<FaEdit size={18} />}
                          onClick={() => handleEditNavigate(item)}
                        />
                        <IconButton
                          aria-label="Delete invoice"
                          variant="ghost"
                          colorScheme="red"
                          icon={<FaTrashAlt size={18} />}
                          onClick={() => setInvoiceToDelete(item)}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-y-auto">
            <Table variant="striped" className="mt-4">
              <Thead>
                <Tr>
                  <Th>Invoice Number</Th>
                  <Th>Customer</Th>
                  <Th>Date of Invoice</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentProducts.map((item) => (
                  <Tr key={item._id}>
                    <Td>{item.invoiceNumber}</Td>
                    <Td>{item.customerDetails.name}</Td>
                    <Td>{moment(item.invoiceDate).format("DD MMM YYYY")}</Td>
                    <Td className="flex gap-2 items-center">
                      <SelectField
                        inputClass={"w-full"}
                        value={{
                          label: copyType.name,
                          value: copyType,
                        }}
                        options={COPY_TYPE}
                        onChange={({ value }) => {
                          setCopyType(value);
                        }}
                        selectStyles={{
                          control: (provided) => ({
                            ...provided,
                            width: "150px",
                          }),
                        }}
                      />
                      <div className="flex flex-wrap gap-1">
                        {isEdit ? (
                          <IconButton
                            aria-label="Cancel"
                            variant="ghost"
                            size="sm"
                            icon={<MdCancel size={18} />}
                            onClick={() => setIsEdit(false)}
                          />
                        ) : (
                          <>
                            <IconButton
                              aria-label="Preview invoice"
                              variant="ghost"
                              size="sm"
                              colorScheme="brand"
                              icon={<FaEye size={18} />}
                              onClick={() => handleSelectedInvoice(item)}
                            />
                            <IconButton
                              aria-label="Share link"
                              variant="ghost"
                              size="sm"
                              colorScheme="brand"
                              icon={<FaShareAlt size={18} />}
                              onClick={() => handleShare(item._id)}
                            />
                            <IconButton
                              aria-label="Print"
                              variant="ghost"
                              size="sm"
                              colorScheme="gray"
                              icon={<FaPrint size={18} />}
                              onClick={() => printPDF(item)}
                            />
                            <IconButton
                              aria-label="Email invoice"
                              variant="ghost"
                              size="sm"
                              colorScheme="gray"
                              icon={<FaMailBulk size={18} />}
                              onClick={() => handleSentToMail(item)}
                            />
                            <IconButton
                              aria-label="Download PDF"
                              variant="ghost"
                              size="sm"
                              colorScheme="gray"
                              icon={<FaDownload size={18} />}
                              onClick={() => handleDownloadPdf(item)}
                            />
                            <IconButton
                              aria-label="Edit invoice"
                              variant="ghost"
                              size="sm"
                              colorScheme="brand"
                              icon={<FaEdit size={18} />}
                              onClick={() => handleEditNavigate(item)}
                            />
                            <IconButton
                              aria-label="Delete invoice"
                              variant="ghost"
                              size="sm"
                              colorScheme="red"
                              icon={<FaTrashAlt size={18} />}
                              onClick={() => setInvoiceToDelete(item)}
                            />
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </div>
      )}
      {!isEmptyInvoices && !noSearchResults ? (
        <ListPagination
          pageCount={pageCount}
          onPageChange={handlePageClick}
          isMobileView={isMobileView}
        />
      ) : null}

      <ConfirmDialog
        isOpen={!!invoiceToDelete}
        onClose={() => setInvoiceToDelete(null)}
        onConfirm={confirmDeleteInvoice}
        title="Delete this invoice?"
        body={`Invoice ${
          invoiceToDelete?.invoiceNumber ?? ""
        } will be permanently removed. This cannot be undone.`}
        confirmText="Delete invoice"
        cancelText="Cancel"
        isLoading={deletingInvoice}
      />
    </div>
  );
};

export default Invoice;
